import { Component, EventEmitter, Input, Output } from '@angular/core';

import { ListPresentationService } from '../../services/list-presentation.service';

/** Komunikat pustki, gdy widok nie poda własnego. */
const DEFAULT_EMPTY = 'COMMON.NO-DATA';

/**
 * Układ szkieletu. Musi odpowiadać treści, która się pojawi — patrz komentarz klasy.
 *
 * `chart` jest jedynym, który nie jest listą: panel z wykresem dostaje jeden blok
 * w miejscu płótna. `rows` go nie zwielokrotnia — wykres jest jeden.
 *
 * `bubbles` to historia rozmowy: dymki naprzemiennie po dwóch stronach, o różnej
 * szerokości. Wiersz listy obiecywałby tu zupełnie inny układ.
 */
export type ListSkeletonShape = 'rows' | 'cards' | 'table' | 'tiles' | 'chart' | 'bubbles';

/**
 * Rodzaj komórki w szkielecie tabeli. Nie jest ozdobnikiem — odwzorowuje to, co
 * naprawdę stoi w wierszu:
 *
 * - `text`      — zwykły tekst komórki,
 * - `number`    — krótka liczba: licznik, ilość (kilka znaków, nie zdanie),
 * - `date`      — sama data, bez ikony,
 * - `date-icon` — data poprzedzona ikoną kalendarza (tabele raportów),
 * - `avatar`    — kółko awatara i nazwisko obok: komórka osoby,
 * - `copy`      — tekst z przyciskiem kopiowania za nim (komórki IMEI),
 * - `badge`     — pigułka statusu albo akcji,
 * - `icon`      — pojedyncza ikona: przycisk akcji, wskaźnik stanu.
 *
 * Szkielet, który tego nie odwzorowuje, obiecuje inny układ, niż dostarcza,
 * i po podmianie tabela podskakuje.
 *
 * Rozróżnienie `date` / `date-icon` nie jest przesadą: ikonę kalendarza rysują tylko
 * dwie tabele raportów, a pozostałych siedem pisze samą datę. Jedna wspólna komórka
 * z ikoną dokładała w nich 24 px, których w prawdziwym wierszu nie ma.
 *
 * Poza zasięgiem celowo zostają dwa kształty spotkane po jednym razie: kwadrat
 * `mat-checkbox` (projekty) i przełącznik telefonów alarmowych (użytkownicy). Oba
 * biorą najbliższy istniejący typ — `icon` i `badge` — bo osobna gałąź dla 18-pikselowego
 * glifu kosztuje więcej niż różnica, którą widać.
 */
export type ListSkeletonCell =
  'text' | 'number' | 'date' | 'date-icon' | 'avatar' | 'copy' | 'badge' | 'icon';

/** Opis kolumn, gdy widok go nie poda — cztery zwykłe kolumny tekstu. */
const DEFAULT_CELLS: ListSkeletonCell[] = ['text', 'text', 'text', 'text'];

/** Jak wygląda jeden rodzaj komórki. Wszystko o nim w jednym miejscu — patrz `CELL_LOOK`. */
interface CellLook {
  /**
   * Udział w szerokości, która zostaje po zmieszczeniu treści wszystkich kolumn
   * (`flex-grow`). Kolumna tabeli nie ma swojej szerokości w oderwaniu od pozostałych:
   * rozkłada się między nie tak, żeby wiersz wypełnił tabelę.
   */
  grow: number;
  /**
   * Szerokość paska. Brak znaczy „treść nieprzewidywalna" — wtedy pasek bierze
   * szerokość z rotacji `TEXT_CELL_WIDTHS`.
   */
  width?: string;
  /** Klasa komórki złożonej: odstęp między glifem a paskiem tekstu. */
  cellClass?: string;
}

/**
 * Wygląd każdego rodzaju komórki — jeden wpis na rodzaj, zamiast trzech równoległych
 * map kluczowanych tym samym. Dodanie rodzaju to jedna linia tutaj i jedna gałąź
 * w szablonie; wcześniej trzeba było pamiętać o trzech miejscach naraz.
 *
 * Szerokości zmierzone tym, co naprawdę stoi w wierszu: data mieści „2026-08-24 13:07",
 * IMEI piętnaście cyfr, nazwisko dwa człony, odznaka jedno słowo, licznik kilka znaków,
 * ikona jest kwadratowa. W komórkach złożonych szerokość dotyczy paska tekstu — glif
 * obok ma swoją stałą w arkuszu.
 *
 * Udziały wzięte z pomiaru tabeli użytkowników (1034 px): nazwisko 280, opaska 246,
 * IMEI 141, ostatni kontakt 131, status 179, menu 56. Bez tego rozkładu wiersz kończył
 * się na 663 px i cały szkielet stał dosunięty do lewej.
 */
const CELL_LOOK: { [cell: string]: CellLook } = {
  'text': { grow: 3 },
  'avatar': { grow: 3, width: '120px', cellClass: 'sk-cell-avatar' },
  'date-icon': { grow: 1.7, width: '110px', cellClass: 'sk-cell-date' },
  'copy': { grow: 1.6, width: '104px', cellClass: 'sk-cell-copy' },
  'badge': { grow: 1.9, width: '96px' },
  'date': { grow: 1.5, width: '110px' },
  'number': { grow: 0.6, width: '32px' },
  'icon': { grow: 0.6, width: '20px' }
};

/**
 * Wygląd rodzaju, którego nie ma w tabeli wyżej. Istnieje po to, żeby nowy rodzaj dodany
 * do `ListSkeletonCell`, a zapomniany tutaj, wyszedł jak zwykły tekst — a nie jako
 * komórka o zerowej szerokości, którą łatwo przeoczyć.
 */
const DEFAULT_LOOK: CellLook = { grow: 1.5 };

/**
 * Szerokości kolumn tekstowych, brane po kolei. Rotacja zamiast jednej wartości, żeby
 * wiersz szkieletu nie wyglądał jak równa krata — prawdziwe komórki też mają różną treść.
 */
const TEXT_CELL_WIDTHS = ['55%', '40%', '65%', '45%', '35%'];

/**
 * Szerokości dymków rozmowy, brane po kolei. Wiadomości mają bardzo różną długość,
 * więc równe dymki czytałyby się jak tabela, a nie jak czat.
 */
const BUBBLE_WIDTHS = ['58%', '34%', '72%', '45%', '63%', '40%'];

/**
 * Minimalny stan listy, jakiego potrzebuje ten komponent.
 *
 * `PagedList` spełnia go wprost — nie wymagał żadnej zmiany. Interfejs istnieje dla
 * widoków, które trzymają listę inaczej (`users/dashboard` ma własną odmianę stronicowania,
 * `projects/dashboard` zwykłą tablicę): zamiast przepisywać ich logikę ładowania, podają
 * cztery pola, które i tak już mają, pod nazwami, których oczekuje komponent.
 *
 * Świadomy kompromis: adapter budowany w getterze tworzy nowy obiekt przy każdej
 * detekcji zmian. Nie szkodzi, bo komponent tylko czyta pola i nie ma `ngOnChanges` —
 * ale gdyby kiedyś dostał `OnPush`, trzeba będzie trzymać stabilną referencję.
 */
export interface ListState {
  /** Pierwsze pobranie — nie ma jeszcze czego pokazać. */
  isInitialLoad: boolean;
  /** Skończone, bez błędu i bez wierszy. */
  isEmpty: boolean;
  /** Status nieudanego pobrania albo null. */
  error: string;
  /** Wiersze — komponent sprawdza tylko, czy są. */
  items: any[];
}

/**
 * Stan listy trzymanej poza `PagedList` — składany z pól, które widok i tak ma.
 *
 * Powstało z czternastu kopii tego samego literału, rozsianych po dziesięciu widokach.
 * Każda kopia była okazją, żeby pominąć jedno pole — najczęściej `error`, a wtedy awaria
 * wygląda w interfejsie dokładnie jak pusty wynik. Warunek pustki ma trzy człony i łatwo
 * o nim zapomnieć; tutaj stoi raz.
 *
 * `PagedList` tej funkcji nie potrzebuje — spełnia kontrakt wprost, własnymi getterami.
 *
 * @param loading czy pobranie właśnie trwa
 * @param items wiersze, jakie widok ma DOTĄD (`null` traktujemy jak brak)
 * @param options `error` — status nieudanego pobrania; `ownEmpty` — widok opisuje pustkę
 *   sam, własnym trafniejszym komunikatem, więc komponent ma o niej milczeć
 */
export function listStateOf(loading: boolean, items: any[],
                            options: { error?: string, ownEmpty?: boolean } = {}): ListState {
  const rows = items || [];
  const error = options.error || null;

  return {
    isInitialLoad: loading && rows.length === 0,
    isEmpty: !options.ownEmpty && !loading && !error && rows.length === 0,
    error,
    items: rows
  };
}

/**
 * Stan sekcji, która listą NIE jest: panel z wykresem, rząd kafelków, mapa. Ma tylko dwa
 * stany — „czekam" i „pokazuję" — bo nie ma wierszy, które można by policzyć, ani pustki,
 * którą trzeba by opisać.
 *
 * Powstało z dwunastu kopii `listStateOf(flaga, [], { ownEmpty: true })`. Każda z nich
 * niosła to samo pytanie („czemu pusta tablica?") i tę samą okazję do pomyłki: bez
 * `ownEmpty` sekcja dostawała komunikat „brak danych" w miejscu wykresu, który dopiero
 * miał się narysować.
 *
 * @param loading czy pobranie właśnie trwa
 */
export function loadingStateOf(loading: boolean): ListState {
  return listStateOf(loading, [], { ownEmpty: true });
}

/**
 * Początek listy: to, co stoi w jej miejscu, zanim pojawią się wiersze.
 *
 * Rodzeństwo `ScrollFooterComponent`, który obsługuje drugi koniec — doklejanie porcji,
 * postęp i ponowienie. Tamten stoi jako ostatnie dziecko kontenera, ten jako pierwsze:
 *
 *     <app-list-state [list]="caregivers" shape="table"
 *                     [columns]="['date', 'text', 'badge']" (retry)="loadMore()"></app-list-state>
 *     <table *ngIf="!caregivers.isInitialLoad"> … </table>
 *     <app-scroll-footer [list]="caregivers" (loadMore)="loadMore()"></app-scroll-footer>
 *
 * Komponent stoi OBOK listy, a nie owija jej. Tak samo jak wartownik — jedna konwencja
 * zamiast dwóch — i bez `ng-template` z `ngTemplateOutlet` w każdym szablonie.
 * Kosztem jest `*ngIf` na samej liście, widoczny w przykładzie wyżej.
 *
 * Renderuje NAJWYŻEJ JEDEN stan, bo trzy warunki wykluczają się nawzajem:
 * `isInitialLoad` wymaga trwającego pobrania, `isEmpty` wyklucza błąd, a gałąź błędu
 * wymaga pustej listy (przy niepustej ponowienie należy do stopki). Nie renderuje nic,
 * gdy lista ma dane — albo gdy `loadingOnly` oddaje komunikaty komuś innemu.
 *
 * O tym, czy ładowanie rysuje szkielet czy spinner, decyduje `ListPresentationService`
 * — jedno pole dla całej aplikacji. Komponent tej decyzji nie podejmuje, tylko ją
 * wykonuje.
 *
 * `shape` nie ma wartości domyślnej celowo. Szkielet nietrafiony w układ docelowy jest
 * gorszy od spinnera: obiecuje coś innego, niż dostarcza, i po podmianie strona
 * podskakuje. Wołający musi zdecydować, bo tylko on wie, co się wyrenderuje.
 */
@Component({
  selector: 'app-list-state',
  templateUrl: './list-state.component.html',
  styleUrls: ['./list-state.component.scss']
})
export class ListStateComponent {

  /** Stan listy. `PagedList` spełnia ten kontrakt wprost, bez adaptera. */
  @Input() list: ListState;

  /** Układ szkieletu — obowiązkowy, patrz komentarz klasy. */
  @Input() shape: ListSkeletonShape;

  /** Ile wierszy (albo kafelków) narysować. Tyle, ile realnie mieści się na ekranie. */
  @Input() rows = 5;

  /**
   * Opis kolumn dla `shape="table"` — po jednym wpisie na kolumnę, w kolejności
   * wyświetlania. Wołający zna docelowy wiersz, więc to on mówi, gdzie stoi data,
   * a gdzie odznaka.
   */
  @Input() columns: ListSkeletonCell[] = DEFAULT_CELLS;

  /**
   * Szczegół awarii dopisywany do komunikatu, np. "/work-history → 500". Sam komunikat
   * mówi tylko, ŻE się nie udało; bez statusu nie da się zgłosić sensownego błędu.
   */
  @Input() errorDetail: string = null;

  /**
   * Klucz i18n komunikatu o pustym wyniku. Domyślnie ogólne „Brak danych", ale widok,
   * który ma trafniejsze zdanie (np. „Brak historii pracy"), podaje własne — inaczej
   * ujednolicenie stanów zubożyłoby treść.
   */
  @Input() emptyMessage: string = null;

  /**
   * Pokaż wyłącznie ładowanie, bez pustki i błędu.
   *
   * Dla sekcji, w których jeden komunikat obsługuje kilka list naraz — jak kafelki
   * alarmów na `my-dashboard`, gdzie SOS, upadki i strefa dzielą wspólne „brak danych"
   * i wspólną informację o awarii. Bez tego ten sam komunikat pojawiłby się tam
   * czterokrotnie: raz wspólny i raz na każdą listę.
   */
  @Input() loadingOnly = false;

  /** Ponowienie po nieudanym pobraniu. Odbiorca woła to samo, co przy wejściu na widok. */
  @Output() retry = new EventEmitter<void>();

  constructor(private presentation: ListPresentationService) {}

  get showsSkeleton(): boolean {
    return this.list.isInitialLoad && this.presentation.loadingStyle === 'skeleton';
  }

  get showsSpinner(): boolean {
    return this.list.isInitialLoad && this.presentation.loadingStyle === 'spinner';
  }

  /** Ponowienie ma sens tylko wtedy, gdy nie ma czego czytać — inaczej robi to stopka. */
  get showsError(): boolean {
    return !this.loadingOnly && !!this.list.error && !this.list.items.length;
  }

  /** Pustka ustępuje, gdy komunikat dla tej sekcji stoi gdzie indziej. */
  get showsEmpty(): boolean {
    return !this.loadingOnly && this.list.isEmpty;
  }

  /**
   * Komunikat pustki. Fallback jest tu, a nie w wartości początkowej wejścia, bo
   * związanie wejścia z pustą zmienną nadpisałoby domyślną i wyczyściło tekst.
   */
  get emptyText(): string {
    return this.emptyMessage || DEFAULT_EMPTY;
  }

  /**
   * Tyle powtórzeń, ile wierszy zamówiono. `*ngFor` czyta wyłącznie długość, więc same
   * wartości nie niosą niczego. `Array.from` zamiast `new Array(n)`, bo to drugie daje
   * tablicę dziurawą — działa w `*ngFor`, ale nie w każdej metodzie tablicowej.
   */
  get placeholders(): unknown[] {
    return Array.from({ length: Math.max(this.rows, 1) });
  }

  get cells(): ListSkeletonCell[] {
    return this.columns && this.columns.length ? this.columns : DEFAULT_CELLS;
  }

  /**
   * Szerokość paska w komórce, stała dla danej pozycji — inaczej szkielet migotałby przy
   * każdej detekcji zmian. Data, odznaka i ikona mają swoje, bo ich treść ma
   * przewidywalną długość; tekst bierze szerokość z rotacji, żeby wiersz nie wyglądał
   * jak równa krata.
   */
  cellWidth(cell: ListSkeletonCell, index: number): string {
    return this.look(cell).width || TEXT_CELL_WIDTHS[index % TEXT_CELL_WIDTHS.length];
  }

  /**
   * Udział komórki w szerokości, która zostaje po zmieszczeniu treści — `flex-grow`.
   *
   * Każda komórka rośnie, żeby wiersz wypełnił tabelę, a pasek w środku zostaje przy
   * swoim rozmiarze i lgnie do lewej — tak samo zachowuje się treść w `<td>`. Bez tego
   * wiersz kończył się w dwóch trzecich tabeli, a reszta zostawała pusta.
   */
  cellGrow(cell: ListSkeletonCell): number {
    return this.look(cell).grow;
  }

  /** Szerokość dymka na danej pozycji — z rotacji, żeby historia nie wyszła równa. */
  bubbleWidth(index: number): string {
    return BUBBLE_WIDTHS[index % BUBBLE_WIDTHS.length];
  }

  /**
   * Czy dymek na danej pozycji jest „mój" (po prawej). Co trzeci, a nie co drugi:
   * naprzemienność co jedną pozycję daje regularny zygzak, który nie wygląda jak
   * rozmowa — w prawdziwej ktoś zwykle pisze kilka wiadomości pod rząd.
   */
  bubbleMine(index: number): boolean {
    return index % 3 === 1;
  }

  /** Klasa komórki złożonej albo pusto — sam odstęp między glifem a paskiem. */
  cellClass(cell: ListSkeletonCell): string {
    return this.look(cell).cellClass || '';
  }

  private look(cell: ListSkeletonCell): CellLook {
    return CELL_LOOK[cell] || DEFAULT_LOOK;
  }
}
